import {
  AccountTreeOutlined,
  AddCircleOutline,
  CheckCircleRounded,
  Remove,
} from '@mui/icons-material';
import { Divider, FormLabel, IconButton, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers';
import { AssortmentTag } from '@retail/my-assortment/types';
import { useProductsTFunction } from '@retail/products/i18n';
import { SupplierItemAssortmentTag } from '@retail/products/types';
import dayjs from 'dayjs';

const labelSx = { fontWeight: 500, fontSize: 12, whiteSpace: 'nowrap' };

const compactDateSx = {
  width: '100%',
  '& .MuiInputBase-root': { height: 28, borderRadius: 1.5, fontSize: 12, pl: 1, pr: 0.5 },
  '& .MuiPickersSectionList-root': { py: 0.5 },
  '& .MuiInputAdornment-root': { ml: 0 },
  '& .MuiIconButton-root': { p: 0.25 },
  '& .MuiSvgIcon-root': { fontSize: 16 },
};

interface Props {
  assortmentTags: AssortmentTag[];
  selectedTags: SupplierItemAssortmentTag[];
  addTag: (tag: AssortmentTag) => void;
  removeTag: (tagId: number) => void;
  updateTagDates?: (tagId: number, dates: { validFrom: Date; validTo?: Date }) => void;
}

export function AssortmentTagSelection({
  assortmentTags,
  selectedTags,
  addTag,
  removeTag,
  updateTagDates,
}: Props) {
  const t = useProductsTFunction();
  const selectedTagIds = selectedTags.map((tag) => tag.tagId);

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'grid',
        gridTemplateColumns: '50% 1px 50%',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Stack flexGrow={1}>
        <Stack
          height={50}
          width="100%"
          direction="row"
          alignItems="center"
          gap={1.5}
          px={3}
          sx={(theme) => ({ bgcolor: alpha(theme.palette.success.main, 0.08) })}
        >
          <AccountTreeOutlined fontSize="small" />
          <Typography variant="subtitle2">
            {t('products.mgItem.assortmentTags.selectedTags')}
          </Typography>
        </Stack>
        <Stack px={3} py={2} gap={1}>
          {selectedTags.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('products.mgItem.assortmentTags.noSelectedTags')}
            </Typography>
          ) : (
            selectedTags.map((tag) => (
              <Stack
                key={tag.tagId}
                gap={1.5}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  border: ({ palette }) => `1px solid ${palette.divider}`,
                }}
              >
                <Stack direction="row" alignItems="center" gap={2}>
                  <Typography variant="body1" flexGrow={1} sx={{ overflowWrap: 'anywhere' }}>
                    {tag.tagName ?? tag.tagId}
                  </Typography>
                  <IconButton sx={{ p: 0 }} onClick={() => removeTag(tag.tagId)}>
                    <Remove fontSize="small" />
                  </IconButton>
                </Stack>

                {updateTagDates && (
                  <Stack direction="row" gap={1}>
                    <Stack gap={0.25} flex={1} minWidth={0}>
                      <FormLabel sx={labelSx} required>
                        {t('products.mgItem.fromDate.label')}
                      </FormLabel>
                      <DatePicker
                        value={tag.validFrom}
                        minDate={new Date()}
                        maxDate={tag.validTo}
                        format="dd/MM/yyyy"
                        onChange={(value) =>
                          !!value &&
                          dayjs(value).isValid() &&
                          updateTagDates(tag.tagId, { validFrom: value, validTo: tag.validTo })
                        }
                        slotProps={{
                          textField: { size: 'small', sx: compactDateSx, helperText: undefined },
                          openPickerButton: { size: 'small' },
                        }}
                      />
                    </Stack>
                    <Stack gap={0.25} flex={1} minWidth={0}>
                      <FormLabel sx={labelSx}>{t('products.mgItem.toDate.label')}</FormLabel>
                      <DatePicker
                        value={tag.validTo ?? null}
                        minDate={tag.validFrom > new Date() ? tag.validFrom : new Date()}
                        format="dd/MM/yyyy"
                        onChange={(value) =>
                          (!value || dayjs(value).isValid()) &&
                          updateTagDates(tag.tagId, {
                            validFrom: tag.validFrom,
                            validTo: value ?? undefined,
                          })
                        }
                        slotProps={{
                          field: { clearable: true },
                          textField: { size: 'small', sx: compactDateSx, helperText: undefined },
                          openPickerButton: { size: 'small' },
                          clearButton: { size: 'small' },
                        }}
                      />
                    </Stack>
                  </Stack>
                )}
              </Stack>
            ))
          )}
        </Stack>
      </Stack>

      <Divider orientation="vertical" sx={{ height: 'auto' }} />

      <Stack>
        <Stack height={50} justifyContent="center" px={2} bgcolor="action.hover">
          <Typography variant="subtitle2">
            {t('products.mgItem.assortmentTags.availableTags')}
          </Typography>
        </Stack>
        <Stack px={3} py={2} gap={0.75}>
          {assortmentTags.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('products.mgItem.assortmentTags.noTags')}
            </Typography>
          ) : (
            assortmentTags.map((tag) => {
              const isAdded = selectedTagIds.includes(tag.id);
              return (
                <Stack
                  key={tag.id}
                  component="button"
                  type="button"
                  disabled={isAdded}
                  onClick={() => addTag(tag)}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  sx={(theme) => ({
                    appearance: 'none',
                    font: 'inherit',
                    textAlign: 'left',
                    width: '100%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'transparent',
                    color: 'text.primary',
                    cursor: isAdded ? 'default' : 'pointer',
                    opacity: isAdded ? 0.6 : 1,
                    transition: theme.transitions.create(['border-color', 'background-color']),
                    '&:hover': isAdded
                      ? undefined
                      : {
                          borderColor: alpha(theme.palette.success.main, 0.5),
                          bgcolor: alpha(theme.palette.success.main, 0.05),
                        },
                  })}
                >
                  <Typography variant="body1" flex={1} sx={{ overflowWrap: 'anywhere' }}>
                    {tag.name}
                  </Typography>
                  {isAdded ? (
                    <CheckCircleRounded fontSize="small" sx={{ color: 'success.main' }} />
                  ) : (
                    <AddCircleOutline fontSize="small" sx={{ color: 'success.main' }} />
                  )}
                </Stack>
              );
            })
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
